"use client";
import React from "react";
import { Box, Container, Typography, Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import Image from "next/image";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { useTranslation } from "react-i18next";
import LeapPageLogoCarousel from "./LeapPageLogoCarousel";

// ─── Assets ─────────────────────────────────────────────────────────────────
const BG_IMG = "https://getgeek-website-assets.b-cdn.net/LeapHeroSectionBg.webp";
const BG_IMG_AR = "https://getgeek-website-assets.b-cdn.net/Getgeek%20Images/LeapHeroSectionBgArabic.webp";
const RIGHT_IMG = "https://getgeek-website-assets.b-cdn.net/LeapHeroSectionRightImg.webp";
const CORRECT_ICON = "https://getgeek-website-assets.b-cdn.net/CorrectIcon.webp";

// ─── Styled Components ──────────────────────────────────────────────────────
const HeroSection = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ theme, isRTL }) => ({
  position: "relative",
  backgroundImage: `url(${isRTL ? BG_IMG_AR : BG_IMG})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  backgroundColor: "#000000",
  paddingTop: "160px",
  overflow: "hidden",
  [theme.breakpoints.down("lg")]: {
    paddingTop: "100px",
    paddingBottom: "60px",
  },
}));

const Badge = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ isRTL }) => ({
  display: "inline-flex",
  alignItems: "center",
  padding: "9px 16px",
  borderRadius: "20px",
  border: "1px solid rgba(124, 58, 237, 0.5)",
  backgroundColor: "rgba(124, 58, 237, 0.15)",
  color: "#C4B5FD",
  fontFamily: isRTL ? "var(--font-outfit)" : "var(--font-outfit)",
  fontSize: "12px",
  fontWeight: 600,
  letterSpacing: "0.5px",
  marginBottom: "24px",
}));

const HeroHeading = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ theme, isRTL }) => ({
  fontFamily: isRTL ? "var(--font-plusjakarta)" : "var(--font-plusjakarta)",
  fontWeight: 700,
  fontSize: "50px",
  lineHeight: 1.2,
  color: "#FFFFFF",
  marginBottom: "20px",
  textAlign: isRTL ? "right" : "left",
  [theme.breakpoints.down("lg")]: {
    fontSize: "34px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "28px",
  },
}));

const HeroDescription = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ isRTL }) => ({
  fontFamily: isRTL ? "var(--font-outfit)" : "var(--font-outfit)",
  fontSize: "16px",
  lineHeight: 1.7,
  color: "#8D8898",
  marginBottom: "32px",
  marginTop: "4%",
  maxWidth: "650px",
  textAlign: isRTL ? "right" : "left",
  marginLeft: isRTL ? "auto" : 0,
}));

const CTAButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ isRTL }) => ({
  backgroundColor: "#9048FB",
  color: "#FFFFFF",
  textTransform: "none",
  fontFamily: isRTL ? "var(--font-outfit)" : "var(--font-outfit)",
  fontSize: "15px",
  fontWeight: 600,
  padding: "12px 28px",
  borderRadius: "8px",
  boxShadow: "0px 6px 18px 0px rgba(144, 72, 251, 0.55)",
  marginBottom: "24px",
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    backgroundColor: "#9048FB",
    boxShadow: "0px 6px 18px 0px rgba(144, 72, 251, 0.55)",
    transform: "scale(1.03)",

  },
}));

const FeatureRow = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: "20px",
  marginBottom: "18px",
  marginTop: '1%',
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "10px",
  },
}));

const FeatureItem = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
}));

const FeatureText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ isRTL }) => ({
  fontFamily: isRTL ? "var(--font-outfit)" : "var(--font-outfit)",
  fontSize: "13.5px",
  fontWeight: 500,
  color: "#8D8898",
  whiteSpace: "nowrap",
}));

const FooterNote = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ isRTL }) => ({
  fontFamily: isRTL ? "var(--font-outfit)" : "var(--font-outfit)",
  fontSize: "13px",
  color: "#8D8898",
  textAlign: isRTL ? "right" : "left",
}));

const RightImageWrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  [theme.breakpoints.down("md")]: {
    marginTop: "8px",
  },
}));

// ── Flex row: left content + right image, side-by-side on md+ ──────────────
const HeroRow = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "40px",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));

const LeftCol = styled(Box)(({ theme }) => ({
  flex: "0 0 50%",
  maxWidth: "50%",
  [theme.breakpoints.down("md")]: {
    flex: "0 0 100%",
    maxWidth: "100%",
  },
}));

const RightCol = styled(Box)(({ theme }) => ({
  flex: "0 0 50%",
  maxWidth: "50%",
  [theme.breakpoints.down("md")]: {
    flex: "0 0 100%",
    maxWidth: "100%",
  },
}));

// ─── Component ───────────────────────────────────────────────────────────────
const LeapPageHeroSection = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const scrollToDemoSection = () => {
    const el = document.getElementById("leap-demo-section");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };
  const features = [
    t("leapHeroSection.features.deliver"),
    t("leapHeroSection.features.noCard"),
    t("leapHeroSection.features.setup"),
  ];

  return (
    <HeroSection isRTL={isRTL}>
      <Container maxWidth="xl">
        <HeroRow>
          {/* Left Content */}
          <LeftCol>
            <Badge isRTL={isRTL}>{t("leapHeroSection.badge")}</Badge>

            <HeroHeading variant="h1" isRTL={isRTL}>
              {t("leapHeroSection.heading")}
            </HeroHeading>

            <HeroDescription isRTL={isRTL}>
              {t("leapHeroSection.description")}
            </HeroDescription>

            <Box>
              <CTAButton
                variant="contained"
                isRTL={isRTL}
                endIcon={
                  isRTL ? (
                    <ArrowBackIcon sx={{ fontSize: "16px", mr: 1.5 }} />
                  ) : (
                    <ArrowForwardIcon sx={{ fontSize: "16px" }} />
                  )
                }
                //  onClick={scrollToDemoSection}
                onClick={() => window.dispatchEvent(new Event("open-leap-demo-modal"))}

              >
                {t("leapHeroSection.ctaButton")}
              </CTAButton>
            </Box>

            <FeatureRow>
              {features.map((text, idx) => (
                <FeatureItem key={idx}>
                  <Image
                    src={CORRECT_ICON}
                    alt="check"
                    width={16}
                    height={16}
                  />
                  <FeatureText isRTL={isRTL}>{text}</FeatureText>
                </FeatureItem>
              ))}
            </FeatureRow>

            <FooterNote isRTL={isRTL}>
              {t("leapHeroSection.footerNote")}
            </FooterNote>
          </LeftCol>

          {/* Right Image */}
          <RightCol>
            <RightImageWrapper>
              <Image
                src={RIGHT_IMG}
                alt="Getgeek AI Recruiter Flow"
                width={700}
                height={520}
                style={{
                  width: "100%",
                  height: "auto",
                  objectFit: "contain",
                }}
                priority
              />
            </RightImageWrapper>
          </RightCol>
        </HeroRow>
      </Container>
      <Box
        sx={{
          mb: {
            xs: "-8%",
            sm: "1%",
            md: "3%",
          },
        }}
      >
        <LeapPageLogoCarousel />
      </Box>
    </HeroSection>
  );
};

export default LeapPageHeroSection;